import { useMemo } from 'react'

import { useModals, useSetState } from '@domain-group/react-hooks'

import { MainContent, ModalContext } from '@domain-group/suit-theme'

import type { PageContextPayload } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import ApprovePublicationBookingModal from '@modals/ApprovePublicationBookingModal'
import CreateCampaignPublicationOrderModal from '@modals/CreateCampaignPublicationOrderModal'
import CreateOrEditPublicationBookingModal from '@modals/CreateOrEditPublicationBookingModal'
import CreatePublicationOrderModal from '@modals/CreatePublicationOrderModal'
import DuplicateOrderItemArtworkModal from '@modals/DuplicateOrderItemArtworkModal'
import ManagePlannerBookingModal from '@modals/ManagePlannerBookingModal'
import ReviewRemovedPublicationBookingItemsModal from '@modals/ReviewRemovedPublicationBookingItemsModal'
import SelectLayoutTemplateModal from '@modals/SelectLayoutTemplateModal'
import SelectPublicationEditionModal from '@modals/SelectPublicationEditionModal'
import SelectPublicationModal from '@modals/SelectPublicationModal'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { PublicationBookingModel } from '@models/types'

import usePublicationBookingPlanner, { defaultRequestOptions } from './hooks/usePublicationBookingPlanner'

import Header from './components/Header'
import LeftSidebar from './components/LeftSidebar'
import Stage from './components/Stage'

const defaultState = {
  showApprovePublicationBookingModal: false,
  showCreateCampaignPublicationOrderModal: false,
  showCreateOrEditPublicationBookingModal: false,
  showCreatePublicationOrderModal: false,
  showDuplicateOrderItemArtworkModal: false,
  showManagePlannerBookingModal: false,
  showReviewRemovedPublicationBookingItemsModal: false,
  showSelectLayoutTemplateModal: false,
  showSelectPublicationEditionModal: false,
  showSelectPublicationModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: string, setState: SetState) => {
  const componentCallbacks = {
    ApprovePublicationBookingModal: {
      closeModal: () => setState({ showApprovePublicationBookingModal: false }),
    },
    CreateCampaignPublicationOrderModal: {
      closeModal: () => setState({ showCreateCampaignPublicationOrderModal: false }),
      createOrder: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditPublicationBookingModal: {
      closeModal: () => setState({ showCreateOrEditPublicationBookingModal: false }),
      createPublicationBooking: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreatePublicationOrderModal: {
      closeModal: () => setState({ showCreatePublicationOrderModal: false }),
    },
    DuplicateOrderItemArtworkModal: {
      closeModal: () => setState({ showDuplicateOrderItemArtworkModal: false }),
    },
    ManagePlannerBookingModal: {
      closeModal: () => setState({ showManagePlannerBookingModal: false }),
      updatePublicationBooking: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ReviewRemovedPublicationBookingItemsModal: {
      closeModal: () => setState({ showReviewRemovedPublicationBookingItemsModal: false }),
    },
    SelectLayoutTemplateModal: {
      closeModal: () => setState({ showSelectLayoutTemplateModal: false }),
    },
    SelectPublicationModal: {
      closeModal: () => setState({ showSelectPublicationModal: false }),
    },
    SelectPublicationEditionModal: {
      closeModal: () => setState({ showSelectPublicationEditionModal: false }),
    },
  }

  // @ts-expect-error TS7053
  return componentCallbacks[component] || {}
}

interface PublicationBookingPlannerProps {
  prop: string,
}

const PublicationBookingPlanner = (props: PublicationBookingPlannerProps) => {
  const { prop } = props

  const [state, setState] = useSetState(defaultState)
  const {
    showApprovePublicationBookingModal,
    showCreateCampaignPublicationOrderModal,
    showCreateOrEditPublicationBookingModal,
    showCreatePublicationOrderModal,
    showDuplicateOrderItemArtworkModal,
    showManagePlannerBookingModal,
    showReviewRemovedPublicationBookingItemsModal,
    showSelectLayoutTemplateModal,
    showSelectPublicationEditionModal,
    showSelectPublicationModal,
  } = state

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData } } = modalContext

  // TODO: Add Type for these modal payloads instead of payload: object
  const bookingPlannerPayload = usePublicationBookingPlanner({
    callbacks: {
      // @ts-expect-error TS2353
      closeModal: (modalKey: string) => setState({ [`show${modalKey}`]: false }),
      showApprovePublicationBookingModal: (payload: {
        isBulkRelease?: boolean,
        publicationBooking: PublicationBookingModel,
      }) => {
        setModalData('ApprovePublicationBookingModal', payload)
        setState({ showApprovePublicationBookingModal: true })
      },
      showCreateCampaignPublicationOrderModal: (payload: object) => {
        setModalData('CreateCampaignPublicationOrderModal', payload)
        setState({ showCreateCampaignPublicationOrderModal: true })
      },
      showCreateOrEditPublicationBookingModal: (payload: object) => {
        setModalData('CreateOrEditPublicationBookingModal', payload)
        setState({ showCreateOrEditPublicationBookingModal: true })
      },
      showCreatePublicationOrderModal: (payload: object) => {
        setModalData('CreatePublicationOrderModal', payload)
        setState({ showCreatePublicationOrderModal: true })
      },
      showManagePlannerBookingModal: (payload: object) => {
        setModalData('ManagePlannerBookingModal', payload)
        setState({ showManagePlannerBookingModal: true })
      },
      showReviewRemovedPublicationBookingItemsModal: (payload: object) => {
        setModalData('ReviewRemovedPublicationBookingItemsModal', payload)
        setState({ showReviewRemovedPublicationBookingItemsModal: true })
      },
      showSelectLayoutTemplateModal: (payload: object) => {
        setModalData('SelectLayoutTemplateModal', payload)
        setState({ showSelectLayoutTemplateModal: true })
      },
      showSelectPublicationModal: (payload: object) => {
        setModalData('SelectPublicationModal', payload)
        setState({ showSelectPublicationModal: true })
      },
      showSelectPublicationEditionModal: (payload: object) => {
        setModalData('SelectPublicationEditionModal', payload)
        setState({ showSelectPublicationEditionModal: true })
      },
    },
  })

  const isMobile = false

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showDuplicateOrderItemArtworkModal: (payload: object) => {
        setModalData('DuplicateOrderItemArtworkModal', payload)
        setState({ showDuplicateOrderItemArtworkModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <MainContent
          offset={{ left: 320, top: 71 }}
          style={{ minHeight: 'calc(100vh - 71px)' }}
          width={!isMobile ? 'calc(100% - 320px)' : '100%'}
        >
          <Header bookingPlannerPayload={bookingPlannerPayload} isMobile={isMobile} />

          {/*
           // @ts-expect-error TS2322 */}
          <LeftSidebar bookingPlannerPayload={bookingPlannerPayload} isMobile={isMobile} />

          <Stage bookingPlannerPayload={bookingPlannerPayload} />
        </MainContent>

        <ApprovePublicationBookingModal
          callbacks={callbacks('ApprovePublicationBookingModal', setState)}
          showModal={showApprovePublicationBookingModal}
        />

        <CreateCampaignPublicationOrderModal
          callbacks={callbacks('CreateCampaignPublicationOrderModal', setState)}
          requestOptions={{
            ...defaultRequestOptions.order,
            ...defaultRequestOptions.orderItem,
          }}
          showModal={showCreateCampaignPublicationOrderModal}
        />

        <CreateOrEditPublicationBookingModal
          callbacks={callbacks('CreateOrEditPublicationBookingModal', setState)}
          showModal={showCreateOrEditPublicationBookingModal}
        />

        <CreatePublicationOrderModal
          callbacks={callbacks('CreatePublicationOrderModal', setState)}
          showModal={showCreatePublicationOrderModal}
        />

        <DuplicateOrderItemArtworkModal
          callbacks={callbacks('DuplicateOrderItemArtworkModal', setState)}
          showModal={showDuplicateOrderItemArtworkModal}
        />

        <ManagePlannerBookingModal
          callbacks={{
            ...callbacks('ManagePlannerBookingModal', setState),
            showSelectLayoutTemplateModal: bookingPlannerPayload.callbacks.showSelectLayoutTemplateModal,
          }}
          showModal={showManagePlannerBookingModal}
        />

        <ReviewRemovedPublicationBookingItemsModal
          callbacks={callbacks('ReviewRemovedPublicationBookingItemsModal', setState)}
          showModal={showReviewRemovedPublicationBookingItemsModal}
        />

        <SelectLayoutTemplateModal
          callbacks={callbacks('SelectLayoutTemplateModal', setState)}
          showModal={showSelectLayoutTemplateModal}
        />

        <SelectPublicationModal
          callbacks={callbacks('SelectPublicationModal', setState)}
          showModal={showSelectPublicationModal}
        />

        <SelectPublicationEditionModal
          callbacks={callbacks('SelectPublicationEditionModal', setState)}
          showModal={showSelectPublicationEditionModal}
          singleSelect
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default PublicationBookingPlanner
